import { useState, useEffect } from 'react';
import { getFeePreviews } from '../../../dashboard/services/reportingApi';
import { parseOptionalFloat, getSettlementUsd } from '../../utils/transactionUtils';

/**
 * Hook to manage fee preview state for resolution actions.
 * Fetches fee breakdowns (accept_partial, deny, keep_full, partial_refund)
 * when a needs_resolution transaction drawer is opened.
 */
export default function useFeePreview(isOpen, transaction, token) {
  const [feePreviews, setFeePreviews] = useState({});
  const [feePreviewLoading, setFeePreviewLoading] = useState(false);
  const [feePreviewError, setFeePreviewError] = useState(null);

  // Reset when transaction changes
  useEffect(() => {
    setFeePreviews({});
    setFeePreviewLoading(false);
    setFeePreviewError(null);
  }, [transaction?.session_id]);

  // Fetch previews when drawer opens for a needs_resolution transaction
  useEffect(() => {
    if (!isOpen || !transaction?.session_id || transaction?.status !== 'needs_resolution' || !token) {
      setFeePreviewLoading(false);
      setFeePreviewError(null);
      return;
    }

    const receivedUsd = getSettlementUsd(transaction) ?? 0;
    const expectedAmountUsd = parseOptionalFloat(transaction.expected_payment_usd)
      ?? (parseOptionalFloat(transaction.order_amount_usd) ?? 0);

    const previewRequests = [];
    if (transaction.discrepancy_type === 'underfunded') {
      previewRequests.push({ action: 'accept_partial', amount_usd: receivedUsd });
      previewRequests.push({ action: 'deny', amount_usd: receivedUsd });
    } else if (transaction.discrepancy_type === 'overfunded') {
      previewRequests.push({ action: 'keep_full', amount_usd: receivedUsd });
      previewRequests.push({ action: 'partial_refund', amount_usd: expectedAmountUsd });
    }

    if (previewRequests.length === 0) {
      setFeePreviews({});
      setFeePreviewLoading(false);
      return;
    }

    let cancelled = false;
    setFeePreviewLoading(true);
    setFeePreviewError(null);

    getFeePreviews(transaction.session_id, previewRequests, token)
      .then((data) => {
        if (cancelled) return;
        const nextPreviews = {};
        (data?.previews || []).forEach((preview) => {
          if (preview?.action) {
            const formatUsd = (value) => {
              const parsed = parseOptionalFloat(value);
              return parsed === null ? '—' : parsed.toFixed(2);
            };

            nextPreviews[preview.action] = {
              action: preview.action,
              amountUsd: formatUsd(preview.amount_usd),
              feePayer: preview.fee_payer || 'merchant',
              merchantFee: formatUsd(preview.merchant_fee_usd),
              customerFee: formatUsd(preview.customer_fee_usd),
              totalFees: formatUsd(preview.total_fees_usd),
              netPayout: formatUsd(preview.net_payout_usd),
              hardCosts: formatUsd(preview.hard_costs_usd),
              refundDue: formatUsd(preview.refund_due_usd),
              overageRefund: formatUsd(preview.overage_refund_usd),
            };
          }
        });
        setFeePreviews(nextPreviews);
      })
      .catch((err) => {
        if (cancelled) return;
        setFeePreviewError(err);
        setFeePreviews({});
      })
      .finally(() => {
        if (!cancelled) setFeePreviewLoading(false);
      });

    return () => { cancelled = true; };
  }, [
    isOpen,
    transaction?.session_id,
    transaction?.status,
    transaction?.discrepancy_type,
    transaction?.merchant_received_usd,
    transaction?.expected_payment_usd,
    transaction?.order_amount_usd,
    token,
  ]);

  return { feePreviews, feePreviewLoading, feePreviewError };
}
